import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/track";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EnvelopeSimple, CheckCircle, CircleNotch } from "@phosphor-icons/react";

export function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email, name: name || null });
      track("newsletter_subscribed", { source: "footer_band" });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="border-t border-border bg-[#0b1220] text-white" data-testid="newsletter-signup">
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-xl">
          <div className="flex items-center gap-2 text-primary">
            <EnvelopeSimple size={22} weight="fill" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Newsletter</span>
          </div>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            Never subscribe to a dud tipster again
          </h2>
          <p className="mt-3 text-white/70">
            Get new verified reviews, Trust Score movers and honest tipster insights straight to your inbox — free, no spam, unsubscribe anytime.
          </p>
        </div>

        <div className="w-full max-w-md">
          {done ? (
            <div className="flex items-center gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-5" data-testid="newsletter-success">
              <CheckCircle size={28} weight="fill" className="shrink-0 text-emerald-400" />
              <p className="text-sm text-white/90">You're on the list. Check your inbox to confirm your subscription.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <Input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 border-white/15 bg-white/10 text-white placeholder:text-white/50"
                data-testid="newsletter-name-input"
              />
              <div className="flex flex-col gap-3 sm:flex-row">
                <Input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 border-white/15 bg-white/10 text-white placeholder:text-white/50"
                  data-testid="newsletter-email-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="h-12 shrink-0 rounded-md px-6 font-semibold"
                  data-testid="newsletter-submit-btn"
                >
                  {loading ? <CircleNotch size={18} className="animate-spin" /> : "Get free updates"}
                </Button>
              </div>
              {error && <p className="text-sm text-red-400" data-testid="newsletter-error">{error}</p>}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
